export type WalkthroughStatus = "idle" | "running" | "completed";

export interface WalkthroughState {
  currentId: string | null;
  stepIndex: number;
  status: WalkthroughStatus;
  seenHints: Record<string, boolean>;
  currentTitle?: string;
  currentBodyHtml?: string;
  steps?: {
    id: string;
    title: string;
    bodyHtml: string;
    controls?: string[];
    highlight?: string;
  }[];
  highlightedElement: string | null;
}

export interface WalkthroughSlice extends WalkthroughState {
  // Actions
  startWalkthrough: (id: string) => void;
  stopWalkthrough: () => void;
  nextStep: () => void;
  prevStep: () => void;
  restartWalkthrough: () => void;
  markStepCompleted: (stepId: string) => void;
  showHint: (stepId: string) => void;
}

const initialWalkthroughState: WalkthroughState = {
  currentId: null,
  stepIndex: 0,
  status: "idle",
  seenHints: {},
  highlightedElement: null,
};

export const createWalkthroughSlice = (set: any): WalkthroughSlice => ({
  ...initialWalkthroughState,

  startWalkthrough: (id) =>
    set(() => ({ currentId: id, stepIndex: 0, status: "running" })),

  stopWalkthrough: () => set(() => ({ ...initialWalkthroughState })),

  nextStep: () =>
    set((state: any) => ({ stepIndex: Math.max(0, state.stepIndex + 1) })),

  prevStep: () =>
    set((state: any) => ({ stepIndex: Math.max(0, state.stepIndex - 1) })),

  restartWalkthrough: () => set((state: any) => ({ stepIndex: 0 })),

  markStepCompleted: (_stepId: string) => set(() => ({})),

  showHint: (stepId: string) =>
    set((state: any) => ({
      seenHints: { ...state.seenHints, [stepId]: true },
    })),
});

// Minimal selectors
export const selectWalkthroughStatus = (state: { status: WalkthroughStatus }) =>
  state.status;

export const selectCurrentStep = (state: WalkthroughState) => {
  const steps = state.steps || [];
  const idx = Math.max(0, Math.min(state.stepIndex, steps.length - 1));
  return steps[idx] || null;
};
